import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concatMap, map, Observable, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { ImageService } from '../../global-store';

import {
  AdvisorAnalysisStatistics,
  AdvisorAppSettingsModel,
  AdvisorDashboardStatistics,
  AdvisorDetailsModel,
  CustomLinkRequestModel,
  CustomLinkResponseModel,
  MainAdvisorModel,
  UpdateAdvisorDetailsRequest,
} from '../../model';

import RequestCache from '../../utils/RequestCache';

@Injectable({
  providedIn: 'root',
})
export class AccountAdvisorService {
  public readonly advisorDetails: RequestCache<AdvisorDetailsModel>;
  public readonly visibleAdvisors: RequestCache<MainAdvisorModel[]>;
  public readonly advisorSignature: RequestCache<Blob | null>;

  constructor(
    private http: HttpClient,
    private readonly imageService: ImageService,
  ) {
    this.advisorDetails = new RequestCache(
      this.http
        .get<AdvisorDetailsModel>(`${environment.apiEndpoint}/account/advisor/self`)
        .pipe(this.imageService.loadImages('profilePictureUrl')),
      'advisorDetails',
    );
    this.visibleAdvisors = new RequestCache(
      http
        .get<MainAdvisorModel[]>(`${environment.apiEndpoint}/account/advisor/visible`)
        .pipe(this.imageService.loadImages('profilePictureUrl')),
    );
    this.advisorSignature = new RequestCache(
      http
        .get(`${environment.apiEndpoint}/account/advisor/self/signature`, {
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(map((response) => (response.status === 204 ? null : response.body))),
    );
  }

  public updateAdvisorDetails(body: UpdateAdvisorDetailsRequest): Observable<void> {
    return this.http
      .put<void>(`${environment.apiEndpoint}/account/advisor/self`, body)
      .pipe(tap(() => this.advisorDetails.clearCache()));
  }

  public getAppSettings(): Observable<AdvisorAppSettingsModel> {
    return this.http.get<AdvisorAppSettingsModel>(`${environment.apiEndpoint}/account/advisor/self/app-settings`);
  }

  public updateAppSettings(body: AdvisorAppSettingsModel): Observable<AdvisorAppSettingsModel> {
    return this.http.put<AdvisorAppSettingsModel>(
      `${environment.apiEndpoint}/account/advisor/self/app-settings`,
      body,
    );
  }

  public updatePartnerLink(linkId: number, linkUrl: string | null): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'text/plain',
    });

    return this.http.put<void>(
      `${environment.apiEndpoint}/account/advisor/self/partner-link/${linkId}`,
      linkUrl || '',
      { headers },
    );
  }

  public getDashboardStatisticsByAdvisorId(advisorId: string | null): Observable<AdvisorDashboardStatistics> {
    let params = new HttpParams();

    if (advisorId !== null) params = params.set('advisor_id', advisorId);

    return this.http.get<AdvisorDashboardStatistics>(
      `${environment.apiEndpoint}/account/advisor/statistics/dashboard`,
      { params },
    );
  }

  public getAnalysisStatisticsByAdvisorId(advisorId: string): Observable<AdvisorAnalysisStatistics> {
    let params: HttpParams = new HttpParams();
    if (advisorId !== null) params = params.set('advisor_id', advisorId);

    return this.http.get<AdvisorAnalysisStatistics>(`${environment.apiEndpoint}/account/advisor/statistics/analysis`, {
      params,
    });
  }

  public updateAdvisorAvatar(file: File): Observable<HttpResponse<string>> {
    this.advisorDetails.updateValue((current) => ({
      ...current,
      profilePictureUrl: URL.createObjectURL(file),
    }));

    return this.http
      .put(`${environment.apiEndpoint}/account/user/self/profile-picture`, null, {
        responseType: 'text',
      })
      .pipe(
        concatMap((uploadUrl: string) =>
          this.http.put(uploadUrl, file, {
            headers: {
              'Content-Type': file.type,
              'x-amz-acl': 'private',
              'Content-Disposition': `attachment; filename="${file.name}"`,
            },
            observe: 'response',
            responseType: 'text',
          }),
        ),
      );
  }

  public deleteAdvisorAvatar(): Observable<void> {
    this.advisorDetails.updateValue((current: AdvisorDetailsModel) => ({ ...current, profilePictureUrl: null }));

    return this.http.delete<void>(`${environment.apiEndpoint}/account/user/self/profile-picture`);
  }

  public updateAdvisorSignature(signature: Blob): Observable<void> {
    this.advisorSignature.setValue(signature);

    return this.http.put<void>(`${environment.apiEndpoint}/account/advisor/self/signature`, signature, {
      headers: new HttpHeaders({
        'Content-Type': 'image/png',
      }),
    });
  }

  public deleteAdvisorSignature(): Observable<void> {
    this.advisorSignature.setValue(null);

    return this.http.delete<void>(`${environment.apiEndpoint}/account/advisor/self/signature`);
  }

  public createCustomLink(body: CustomLinkRequestModel): Observable<CustomLinkResponseModel> {
    return this.http
      .post<CustomLinkResponseModel>(`${environment.apiEndpoint}/account/advisor/self/custom-link`, body)
      .pipe(tap((): void => this.advisorDetails.clearCache()));
  }

  public updateCustomLink(id: number, body: CustomLinkRequestModel): Observable<CustomLinkResponseModel> {
    return this.http
      .put<CustomLinkResponseModel>(`${environment.apiEndpoint}/account/advisor/self/custom-link/${id}`, body)
      .pipe(tap((): void => this.advisorDetails.clearCache()));
  }

  public deleteCustomLink(id: number): Observable<void> {
    return this.http
      .delete<void>(`${environment.apiEndpoint}/account/advisor/self/custom-link/${id}`)
      .pipe(tap((): void => this.advisorDetails.clearCache()));
  }
}
